import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Upload, FileText, Trash2, Loader2, Eye, Image as ImageIcon, FileSpreadsheet, FileType2 } from 'lucide-react';
import ConfirmDeleteDialog from '@/components/ConfirmDeleteDialog';
import DocumentViewer, { kindOf, extOf } from '@/components/DocumentViewer';
import { API } from '@/App';

const ICON_BY_KIND = { pdf: FileType2, image: ImageIcon, sheet: FileSpreadsheet, docx: FileText, other: FileText };
const TINT_BY_KIND = {
  pdf: 'bg-rose-50 text-rose-700 border-rose-200',
  image: 'bg-teal-50 text-teal-700 border-teal-200',
  sheet: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  docx: 'bg-sky-50 text-sky-700 border-sky-200',
  other: 'bg-muted text-foreground border-border',
};

/**
 * Upload + preview gallery. Use inside Vendor Detail (per-hotel tariff
 * sheets), Customer Profile, Tour Detail — anywhere a file bag belongs.
 *
 * Props:
 *   scope         — { vendor_id?, customer_id?, tour_id? } — which entity
 *                   these files attach to. Exactly one should be set.
 *   category      — default upload category ("Tariff" for hotels, "Document"
 *                   otherwise). Accepts Invoice | Itinerary | Tariff | Document.
 *   title         — card heading. Defaults to "Documents".
 *   emptyHint     — shown when no files are present.
 *   accept        — HTML file-input accept string.
 */
const DocumentGallery = ({
  scope,
  category = 'Document',
  title = 'Documents',
  emptyHint = 'No files uploaded yet.',
  accept = '.pdf,.doc,.docx,.jpg,.jpeg,.png,.webp,.xlsx,.xls,.csv',
  testId = 'document-gallery',
}) => {
  const POLLEN_API = `${API}/pollen`;
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [viewerFile, setViewerFile] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  const authFetch = (url, opts = {}) => {
    const h = {};
    const t = localStorage.getItem('session_token');
    if (t) h['Authorization'] = `Bearer ${t}`;
    return fetch(url, { ...opts, credentials: 'include', headers: { ...opts.headers, ...h } });
  };

  const buildQuery = useCallback(() => {
    const q = new URLSearchParams();
    if (scope?.vendor_id) q.set('vendor_id', scope.vendor_id);
    if (scope?.customer_id) q.set('customer_id', scope.customer_id);
    if (scope?.tour_id) q.set('tour_id', scope.tour_id);
    return q.toString();
  }, [scope?.vendor_id, scope?.customer_id, scope?.tour_id]);

  const fetchFiles = useCallback(async () => {
    const q = buildQuery();
    if (!q) { setFiles([]); setLoading(false); return; }
    setLoading(true);
    try {
      const res = await authFetch(`${POLLEN_API}/files?${q}`);
      if (res.ok) setFiles(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [buildQuery]);

  useEffect(() => { fetchFiles(); }, [fetchFiles]);

  const handleUpload = async (e) => {
    const list = Array.from(e.target.files || []);
    if (list.length === 0) return;
    setUploading(true);
    let ok = 0, fail = 0;
    for (const f of list) {
      const fd = new FormData();
      fd.append('file', f);
      if (scope?.vendor_id) fd.append('vendor_id', scope.vendor_id);
      if (scope?.customer_id) fd.append('customer_id', scope.customer_id);
      if (scope?.tour_id) fd.append('tour_id', scope.tour_id);
      fd.append('category', category);
      try {
        const r = await authFetch(`${POLLEN_API}/files/upload`, { method: 'POST', body: fd });
        if (r.ok) ok += 1; else fail += 1;
      } catch { fail += 1; }
    }
    if (ok) toast.success(`Uploaded ${ok} file${ok === 1 ? '' : 's'}${fail ? ` · ${fail} failed` : ''}`);
    else toast.error('Upload failed');
    setUploading(false);
    e.target.value = '';
    fetchFiles();
  };

  const doDownload = async (f) => {
    try {
      const res = await authFetch(`${POLLEN_API}/files/${f.id}/download`);
      if (!res.ok) throw new Error('download failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = f.original_filename; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Could not download'); }
  };

  const doDelete = async () => {
    if (!deleteId) return;
    try {
      const res = await authFetch(`${POLLEN_API}/files/${deleteId}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('delete failed');
      toast.success('Removed');
    } catch { toast.error('Could not delete'); }
    setDeleteId(null);
    fetchFiles();
  };

  return (
    <div className="space-y-3" data-testid={testId}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold">{title}</h3>
          <Badge variant="outline" className="text-[10px]">{files.length}</Badge>
        </div>
        <label className="cursor-pointer">
          <input
            type="file"
            multiple
            className="hidden"
            accept={accept}
            onChange={handleUpload}
            disabled={uploading}
            data-testid={`${testId}-upload-input`}
          />
          <Button asChild variant="outline" size="sm" disabled={uploading}>
            <span>
              {uploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
              Upload
            </span>
          </Button>
        </label>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
          <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading…
        </div>
      ) : files.length === 0 ? (
        <div className="text-center py-6 text-xs text-muted-foreground border border-dashed rounded-md">
          {emptyHint}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {files.map(f => {
            const kind = kindOf(f);
            const Icon = ICON_BY_KIND[kind];
            const ext = (extOf(f.original_filename) || '').toUpperCase();
            const label = (f.display_name && f.display_name.trim()) || f.original_filename;
            return (
              <div key={f.id}
                className="group relative border rounded-lg bg-white hover:shadow-md transition-all overflow-hidden"
                data-testid={`${testId}-card-${f.id}`}>
                <button
                  type="button"
                  onClick={() => setViewerFile(f)}
                  className="w-full text-left"
                  data-testid={`${testId}-preview-${f.id}`}
                >
                  <div className={`aspect-[3/4] flex flex-col items-center justify-center gap-2 ${TINT_BY_KIND[kind]} border-b`}>
                    <Icon className="w-10 h-10 opacity-70" />
                    <span className="text-[10px] font-mono tracking-wider">{ext || kind.toUpperCase()}</span>
                    <span className="text-[10px] opacity-70">Click to preview</span>
                  </div>
                  <div className="p-2">
                    <p className="text-xs font-medium truncate" title={label}>{label}</p>
                    <p className="text-[10px] text-muted-foreground">
                      {f.size != null ? `${(f.size / 1024).toFixed(1)} KB` : ''}
                      {f.created_at ? ` · ${f.created_at.slice(0, 10)}` : ''}
                    </p>
                    {f.category && <Badge variant="outline" className="mt-1 text-[9px] px-1 py-0">{f.category}</Badge>}
                  </div>
                </button>
                <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button variant="secondary" size="sm" className="h-6 w-6 p-0" onClick={(e) => { e.stopPropagation(); setViewerFile(f); }} title="Preview & rename">
                    <Eye className="w-3 h-3" />
                  </Button>
                  <Button variant="secondary" size="sm" className="h-6 w-6 p-0 text-destructive" onClick={(e) => { e.stopPropagation(); setDeleteId(f.id); }} title="Delete">
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <DocumentViewer
        open={!!viewerFile}
        file={viewerFile}
        onClose={() => setViewerFile(null)}
        onDownload={doDownload}
        onRenamed={() => fetchFiles()}
      />
      <ConfirmDeleteDialog
        open={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={doDelete}
        title="Remove document?"
        description="This file will be permanently removed."
      />
    </div>
  );
};

export default DocumentGallery;
